import React from "react";
import {
    WbAction,
    WbDocument,
    WbSubject,
    WbSubjectOverallStatus,
    WbTask,
} from "../../workbench/types/WbTypes";

export const fallbackSubjectView = {
    title: "ERGO 2026 Property QS",
    broker: "Willis Towers Watson",
    cedent: "ERGO Global Corporate",
    claimant: "ERGO Global Corporate",
    identifier: "SUB-2026-0457",
    status: "Pre-bind",
    stage: "In Discussion",
    uwYear: 2026,
    mainClass: "Property Damage, Business Interruption",
    currency: "USD",
    period: "01 Jan 2026 - 31 Dec 2026",
    coverage: "Worldwide excluding US & Canada",
    businessType: "Excess of Loss",
    totalLimit: "USD 125,000,000",
    totalPremium: "USD 1,250,000",
};

export const subjectStatusLabelByValue: Record<WbSubjectOverallStatus, string> =
    {
        New: "New",
        InProgress: "In Progress",
        Completed: "Completed",
        OnHold: "On Hold",
    };

export const fallbackDocuments: WbDocument[] = [
    {
        taskId: "prototype-task-1",
        taskTitle: "Review draft slip",
        projectId: "prototype-project",
        packageId: "prototype-document-1",
        fileName: "ERGO 2026 Property Draft Slip.docx",
        contentType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        fileSize: 245760,
        uploadTime: "2026-05-13T09:15:00Z",
    },
    {
        taskId: "prototype-task-2",
        taskTitle: "Confirm Layer 2 premium",
        projectId: "prototype-project",
        packageId: "prototype-document-2",
        fileName: "Layer schedule.xlsx",
        contentType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        fileSize: 98304,
        uploadTime: "2026-05-12T16:42:00Z",
    },
];

export const fallbackTasks: WbTask[] = [
    {
        id: "prototype-task-1",
        title: "Confirm Layer 2 premium",
        projectId: "prototype-project",
        assignee: "Grace Roberts",
        status: "In Progress",
        isDone: false,
    },
    {
        id: "prototype-task-2",
        title: "Review subjectivities",
        projectId: "prototype-project",
        assignee: "Mikael Hansen",
        status: "Open",
        isDone: false,
    },
];

export const fallbackActions: WbAction[] = [
    {
        actionEventId: "prototype-action-1",
        actionName: "Document uploaded",
        triggeredOn: "2026-05-13T09:15:00Z",
        status: "Completed",
        taskId: "prototype-task-1",
        taskTitle: "ERGO 2026 Property Draft Slip.docx",
    },
    {
        actionEventId: "prototype-action-2",
        actionName: "AI analysis completed",
        triggeredOn: "2026-05-12T16:42:00Z",
        status: "Completed",
        taskId: "prototype-task-2",
        taskTitle: "13 May analysis session",
    },
    {
        actionEventId: "prototype-action-3",
        actionName: "Task updated",
        triggeredOn: "2026-05-12T10:20:00Z",
        status: "Started",
        taskId: "prototype-task-3",
        taskTitle: "Confirm Layer 2 premium",
    },
];

export const formatDate = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export const formatDateTime = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export const formatActivityTime = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    const now = new Date();
    const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
    );
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    if (date >= startOfToday) {
        return `Today, ${date.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
        })}`;
    }

    if (date >= startOfYesterday) {
        return `Yesterday, ${date.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
        })}`;
    }

    return date.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export const formatFileSize = (bytes: number) => {
    if (!bytes) {
        return "-";
    }

    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }

    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const getSubjectParty = (subject?: WbSubject | null) =>
    subject?.cedent ??
    subject?.context?.cedent ??
    subject?.context?.claimant ??
    subject?.context?.broker ??
    "";

export const getSubjectTitle = (subject?: WbSubject | null) => {
    if (!subject) {
        return fallbackSubjectView.title;
    }

    const party = getSubjectParty(subject);
    const mainClass = subject.context?.mainClass?.trim();
    const year = subject.context?.uwYear;
    const base = party || subject.identifier;

    if (mainClass && year) {
        return `${base} ${mainClass} ${year}`;
    }

    if (mainClass) {
        return `${base} ${mainClass}`;
    }

    return base || fallbackSubjectView.title;
};

export const getCoveragePeriod = (subject?: WbSubject | null) => {
    const dates = [
        formatDate(subject?.periodFrom),
        formatDate(subject?.periodTo),
    ]
        .filter(Boolean)
        .join(" - ");

    return dates || fallbackSubjectView.period;
};

export const getCompletionPercent = (subject?: WbSubject | null) => {
    const totalTasks = subject?.state.totalTasks ?? 0;
    const openTasks = subject?.state.openTasks ?? 0;

    if (!totalTasks) {
        return 50;
    }

    return Math.round(((totalTasks - openTasks) / totalTasks) * 100);
};

export const getSubjectStatusLabel = (subject?: WbSubject | null) =>
    subject
        ? subjectStatusLabelByValue[subject.state.overallStatus]
        : fallbackSubjectView.status;

export const noop = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
};

export const copyTextToClipboard = async (text: string) => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
};
